import { useEffect } from 'react';
import { t } from '@app/i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@zarya/theme';
import { Alert, ErrorBanner } from '@zarya/ui';
import { useHealth } from '@app/api/hooks/shell';
import { useCurrentUser } from '@app/auth/useCurrentUser';
import { isErrorCode } from '@app/api/error-codes';
import { googleStartUrl, useAvailableGoogle, useLogin } from './Login.api';
import { returnRoute, type LoginFormValues } from './LoginModel';
import { LoginForm } from './LoginForm';
import s from './Login.module.css';

/*
 * De onde vêm os ativos desta tela (10/09/2026) — comentário de arquivo, não de símbolo.
 *
 * Os dois saíram do tema Keycloak da Segura, o mesmo do console de identidade, de
 * `identity.dev-qampion.tech/resources/dolpq/login/keycloakify-zarya-theme/dist/assets/`:
 *
 * - `segura.svg` virou `public/marca/segura.svg`, servido como estático e referenciado por
 *   `<use>` (ver `SeguraMark`). O cabeçalho do próprio arquivo diz o que mudou nele.
 * - `CloudSecurity.svg` virou `docs/design/ref-login-segura-keycloak.svg`, verbatim, como
 *   REFERÊNCIA de layout — não como ativo servido. Apesar do nome, ele não é uma ilustração:
 *   é o painel direito inteiro daquela tela, com todo o texto convertido em contorno
 *   («Welcome to Segura® Cloud Security» e mais dois parágrafos). Exibi-lo aqui traria texto
 *   duplicado, em inglês, fora do catálogo i18n e de outro produto. Por isso o painel abaixo
 *   REPRODUZ o arranjo dele (fundo verde da marca, assinatura teal, título serifado em dois
 *   degraus) com o texto vivo daqui. Trilha em `docs/progress/login-marca.md`.
 *
 * O arranjo da coluna esquerda continua vindo de
 * `docs/design/ref-login-identity-platform.png`.
 */

/** Caminho público da marca. O `#mark` é o alvo do `<use>`, como o `#i-<nome>` do sprite. */
const MARK_HREF = '/marca/segura.svg#mark';

/**
 * Marca Segura, pelo mesmo mecanismo do `Icon`: um `<use>` para arquivo estático de `public/`
 * (o navegador cacheia; nada entra no bundle a cada build). O desenho não traz `fill` próprio,
 * então herda o de quem o referencia — é isso que deixa a marca acompanhar o tema sem uma
 * segunda cópia do arquivo e sem filtro de CSS. Decorativa nos dois usos: quem nomeia a tela
 * é o `h1`, e no painel o texto «Segura» está ao lado.
 */
function SeguraMark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 180 180"
      focusable="false"
      aria-hidden="true"
      data-testid="segura-mark"
    >
      <use href={MARK_HREF} />
    </svg>
  );
}

/**
 * T1 — Login (Story 1).
 *
 * Duas colunas: à esquerda a marca Segura e o formulário, à direita o painel de boas-vindas
 * no verde da marca, que some em telas estreitas. Cobre FR-001 a FR-007, FR-011 e FR-109.
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { estado, recarregar } = useCurrentUser();

  const returnValue = returnRoute(searchParams.get('retorno'));
  const expired = searchParams.get('expirada') === '1';
  // `googleCallback` devolve o usuário a `/login?error=<code>` quando o SSO falha.
  const callbackError = searchParams.get('error');

  const health = useHealth();
  const google = useAvailableGoogle();
  const login = useLogin();

  // Quem já tem sessão não fica na porta: entrou por link ou apertou "voltar".
  useEffect(() => {
    if (estado === 'autenticado') navigate(returnValue, { replace: true });
  }, [estado, navigate, returnValue]);

  const withoutConnection = health.isError;

  function signIn(values: LoginFormValues) {
    login.mutate(
      {
        email: values.email,
        password: values.senha,
        remember: values.lembrar,
        return_to: returnValue,
      },
      {
        onSuccess: () => {
          // A sessão passa a valer agora; o `SessionProvider` relê o `getMe` do cache.
          recarregar();
          navigate(returnValue, { replace: true });
        },
      },
    );
  }

  return (
    <main className={s.tela}>
      <section className={s.coluna}>
        <div className={s.caixa}>
          <div className={s.marca}>
            <SeguraMark className={s.marcaIcone} />
            <h1 className={s.marcaNome}>360 Agentic Identity</h1>
          </div>

          {expired ? (
            <Alert tone="warning" title={t('login.page.expired.title')}>
              {t('login.page.expired.message')}
            </Alert>
          ) : null}

          {withoutConnection ? (
            <Alert tone="danger" title={t('login.page.offline.title')}>
              {t('login.page.offline.message')}
            </Alert>
          ) : null}

          {callbackError && isErrorCode(callbackError) ? (
            <ErrorBanner error={callbackError} title={t('login.page.google.error')} />
          ) : null}

          {login.error ? <ErrorBanner error={login.error} title={t('login.page.error')} /> : null}

          <LoginForm onSubmit={signIn} pending={login.isPending} bloqueado={withoutConnection} />

          {google.data ? (
            <>
              <div className={s.separador}>
                <span>{t('login.page.orSignInWith')}</span>
              </div>
              {/* Âncora de verdade: o OIDC precisa de navegação do documento, não
                  de XHR — e assim o usuário pode até abrir noutra aba. */}
              <a className={s.google} href={googleStartUrl(returnValue)}>
                <Icon name="key" size="sm" aria-hidden="true" />
                {t('login.page.google')}
              </a>
            </>
          ) : null}

          <p className={s.rodape}>
            {health.data?.version
              ? t('login.page.version', { version: health.data.version })
              : null}
          </p>
        </div>
      </section>

      <aside className={s.painel} aria-label={t('login.panel.label')}>
        <div className={s.painelConteudo}>
          <span className={s.painelMarca}>
            <SeguraMark />
            <b>Segura</b>
          </span>
          {/* Dois degraus, como o painel de referência («Welcome to Segura®» sobre «Cloud
              Security»): as mesmas palavras de antes, só a quebra e o tamanho são novos.
              Os dois <span> são o que o CSS mede — não acrescente um terceiro sem olhar
              `.painelTitulo` no `Login.module.css`.

              O `{' '}` entre os degraus não é enfeite e não se apaga: sem ele o JSX
              descarta a linha só-de-espaço e o `textContent` do heading cola em
              «Segura®360 Agentic Identity» — que é o que o leitor de tela lê e o que
              alguém copia. Na tela não muda nada (item de flex só com espaço não é
              renderizado), e é por isso que some sem ninguém ver. Guardado pelo teste
              «mantém o espaço entre os dois degraus». */}
          <h2 className={s.painelTitulo}>
            <span>
              Welcome to Segura<sup>®</sup>
            </span>{' '}
            <span>360 Agentic Identity</span>
          </h2>
          <p className={s.painelTexto}>{t('login.panel.text')}</p>
        </div>
      </aside>
    </main>
  );
}
